/**
 * Error classification system for Sandpack errors
 * Determines if errors are recoverable and provides context for AI fixing
 */

export interface SandpackError {
    type: string;
    message: string;
    line?: number;
    column?: number;
    path?: string;
    title?: string;
    severity?: string;
    compilatonError?: boolean;
}

export interface ErrorClassification {
    isRecoverable: boolean;
    errorType: 'syntax' | 'import' | 'runtime' | 'type' | 'bundler' | 'network' | 'unknown';
    confidenceScore: number; // 0-1, how confident we are in recovery
    suggestedFix?: string;
    context: string; // concise user-facing summary (no stack traces)
    fullContext?: string; // full detail with stack traces for copilot prompting
}

/**
 * Patterns that indicate recoverable errors
 */
const RECOVERABLE_PATTERNS = {
    import: [
        /cannot find module/i,
        /module not found/i,
        /can't resolve/i,
        /could not resolve/i,
        /failed to resolve/i,
        /cannot resolve dependency/i,
        /import.*not found/i,
        /export.*does not exist/i,
        /has no exported member/i,
    ],
    syntax: [
        /unexpected token/i,
        /syntax error/i,
        /expected.*got/i,
        /missing/i,
        /unterminated/i,
        /invalid or unexpected token/i,
    ],
    type: [
        /type.*is not assignable/i,
        /property.*does not exist/i,
        /argument of type/i,
        /cannot find name/i,
        /is declared but never used/i,
    ],
    runtime: [/is not defined/i, /cannot read propert/i, /undefined is not/i, /null is not/i, /is not a function/i],
};

/**
 * Patterns that indicate non-recoverable errors
 */
const NON_RECOVERABLE_PATTERNS = [
    /network error/i,
    /bundler.*timeout/i,
    /out of memory/i,
    /connection refused/i,
    /fetch failed/i,
    /cors/i,
];

/**
 * Classifies a Sandpack error to determine if it's recoverable
 */
export function classifyError(error: SandpackError): ErrorClassification {
    const message = error.message || '';
    const title = error.title || '';
    const fullText = `${title} ${message}`.toLowerCase();
    const { summary, full } = extractErrorContext(error);

    // Check for non-recoverable errors first
    for (const pattern of NON_RECOVERABLE_PATTERNS) {
        if (pattern.test(fullText)) {
            return {
                isRecoverable: false,
                errorType: 'network',
                confidenceScore: 0.9,
                context: summary,
                fullContext: full,
            };
        }
    }

    // Check for recoverable error types
    for (const [errorType, patterns] of Object.entries(RECOVERABLE_PATTERNS)) {
        for (const pattern of patterns) {
            if (pattern.test(fullText)) {
                const confidence = calculateConfidence(errorType as any, error);
                return {
                    isRecoverable: confidence >= 0.5,
                    errorType: errorType as any,
                    confidenceScore: confidence,
                    suggestedFix: generateSuggestedFix(errorType as any, error),
                    context: summary,
                    fullContext: full,
                };
            }
        }
    }

    // Unknown error - treat as potentially recoverable but with low confidence
    return {
        isRecoverable: true,
        errorType: 'unknown',
        confidenceScore: 0.3,
        context: summary,
        fullContext: full,
    };
}

/**
 * Calculate confidence score based on error type and details
 */
function calculateConfidence(errorType: string, error: SandpackError): number {
    let confidence = 0.7; // Base confidence

    // Import errors are highly recoverable
    if (errorType === 'import') {
        confidence = 0.9;
    }

    // Syntax errors are moderately recoverable
    if (errorType === 'syntax') {
        confidence = 0.7;
    }

    // Type errors are usually recoverable in TS projects
    if (errorType === 'type') {
        confidence = 0.8;
    }

    // Runtime errors are less predictable
    if (errorType === 'runtime') {
        confidence = 0.5;
    }

    // Boost confidence if we have location information
    if (error.line || error.path) {
        confidence += 0.1;
    }

    return Math.min(confidence, 1.0);
}

/**
 * Generate a suggested fix description for the AI
 */
function generateSuggestedFix(errorType: string, error: SandpackError): string {
    const message = error.message || '';

    switch (errorType) {
        case 'import': {
            // Try to extract module name
            const moduleMatch = message.match(/['"]([^'"]+)['"]/);
            const moduleName = moduleMatch ? moduleMatch[1] : 'the module';
            return `Fix the import statement for ${moduleName}. Check the module path and ensure it's correctly imported.`;
        }

        case 'syntax':
            return `Fix the syntax error${error.line ? ` at line ${error.line}` : ''}. Ensure proper JavaScript/TypeScript syntax.`;

        case 'type':
            return `Fix the TypeScript type error. Ensure types are correctly defined and used.`;

        case 'runtime':
            return `Fix the runtime error by ensuring all variables are properly defined before use.`;

        default:
            return `Fix the error in the code.`;
    }
}

/**
 * Strip React component stack traces and verbose framework noise from error messages.
 * Keeps the first meaningful line(s) for user display.
 * Returns { summary, full } — summary for user, full for copilot.
 */
function stripStackTrace(message: string): { summary: string; full: string } {
    if (!message) return { summary: '', full: '' };

    const full = message;

    // Split on newlines and find where the stack trace starts
    const lines = message.split('\n');
    const summaryLines: string[] = [];

    for (const line of lines) {
        const trimmed = line.trim();
        // Stop at React component stack traces
        if (/^\s*at\s+\w/.test(line) && /https?:\/\//.test(line)) break;
        // Stop at Sandpack source code preview lines (e.g. "81 |    }}" or "> 83 |    ...")
        if (/^>?\s*\d+\s*\|/.test(trimmed)) break;
        // Stop at caret lines used in code previews (e.g. "   |   ^")
        if (/^\s*\|\s*\^+\s*$/.test(trimmed)) break;
        // Stop at "Consider adding an error boundary" boilerplate
        if (/consider adding an error boundary/i.test(trimmed)) break;
        // Stop at "Visit https://reactjs.org" boilerplate
        if (/visit https?:\/\/react/i.test(trimmed)) break;
        // Skip empty lines at the end
        if (trimmed === '' && summaryLines.length > 0 && summaryLines[summaryLines.length - 1] === '') continue;

        summaryLines.push(trimmed);
    }

    // Trim trailing empty lines
    while (summaryLines.length > 0 && summaryLines[summaryLines.length - 1] === '') {
        summaryLines.pop();
    }

    const summary = summaryLines.join('\n') || lines[0] || message.substring(0, 200);

    return { summary, full };
}

/**
 * Extract error context — returns { summary, full }.
 * summary: concise single-line description (for aggregation into a compact prompt)
 * full: complete detail with stack traces for copilot prompting
 */
function extractErrorContext(error: SandpackError): { summary: string; full: string } {
    const { summary: cleanMsg } = stripStackTrace(error.message || '');
    const firstLine = cleanMsg.split('\n')[0].trim();

    const location = error.path
        ? `${error.path}${error.line ? `:${error.line}` : ''}${error.column ? `:${error.column}` : ''}`
        : '';

    const summary = location ? `${location} — ${firstLine}` : firstLine;
    const full = error.message || summary;

    return { summary, full };
}

/**
 * Extract a normalised key from an error message for deduplication.
 * Strips stack traces, code previews, and whitespace so that the same
 * logical error reported through different Sandpack channels collapses
 * into a single entry.
 */
function errorDeduplicationKey(message: string): string {
    return (message || '').split('\n')[0].replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * Deduplicate errors, preferring entries that carry file/line metadata.
 */
function deduplicateErrors(errors: SandpackError[]): SandpackError[] {
    const map = new Map<string, SandpackError>();
    for (const err of errors) {
        const key = errorDeduplicationKey(err.message);
        if (!key) continue;
        const existing = map.get(key);
        if (!existing || (!existing.path && err.path)) {
            map.set(key, err);
        }
    }
    return Array.from(map.values());
}

/**
 * Aggregates multiple errors into a single classification
 */
export function classifyErrors(errors: SandpackError[]): ErrorClassification {
    if (errors.length === 0) {
        return {
            isRecoverable: true,
            errorType: 'unknown',
            confidenceScore: 0,
            context: 'No errors detected',
        };
    }

    const uniqueErrors = deduplicateErrors(errors);
    const classifications = uniqueErrors.map(classifyError);

    // If any error is non-recoverable, mark as non-recoverable
    const hasNonRecoverable = classifications.some((c) => !c.isRecoverable);
    if (hasNonRecoverable) {
        const nonRecoverable = classifications.find((c) => !c.isRecoverable)!;
        return {
            ...nonRecoverable,
            context: classifications.map((c) => c.context).join('\n\n---\n\n'),
            fullContext: classifications.map((c) => c.fullContext || c.context).join('\n'),
        };
    }

    const avgConfidence = classifications.reduce((sum, c) => sum + c.confidenceScore, 0) / classifications.length;
    const primaryType = classifications[0].errorType;

    return {
        isRecoverable: true,
        errorType: primaryType,
        confidenceScore: avgConfidence,
        suggestedFix: classifications[0].suggestedFix,
        context: classifications.map((c) => c.context).join('\n\n---\n\n'),
        fullContext: classifications.map((c) => c.fullContext || c.context).join('\n'),
    };
}

/**
 * Strips code previews, stack traces, and framework noise from an error message.
 * Returns only the first meaningful lines suitable for user display.
 */
export function sanitizeErrorForDisplay(message: string): string {
    if (!message) return '';
    return stripStackTrace(message).summary;
}

/**
 * Marker that separates the user-visible summary from the full copilot detail
 * in auto-fix prompts. Everything after this marker (inclusive) should be
 * stripped before displaying the message in the chat thread.
 */
export const COPILOT_DETAIL_MARKER = '<!-- COPILOT_DETAIL -->';

/**
 * Generates an AI prompt for fixing errors.
 *
 * The prompt has two sections:
 * 1. User-visible summary (clean, no stack traces) — shown in the chat bubble
 * 2. Copilot detail (full stack traces) — hidden from user, sent to the LLM
 *
 * Use {@link COPILOT_DETAIL_MARKER} to split and strip section 2 before display.
 */
export function generateFixPrompt(classification: ErrorClassification, attemptNumber: number = 1): string {
    const retryContext = attemptNumber > 1 ? ` (Attempt ${attemptNumber})` : '';

    let userSummary = `An error has been detected in the project and requires fixing${retryContext}.\n\n`;
    userSummary += classification.context;

    let detail = `\n\n${COPILOT_DETAIL_MARKER}\n`;
    detail += `Error type: ${classification.errorType}\n`;

    if (classification.fullContext && classification.fullContext !== classification.context) {
        detail += `\nFull error output:\n${classification.fullContext}\n\n`;
    }

    if (classification.suggestedFix) {
        detail += `Suggested Fix: ${classification.suggestedFix}\n`;
    }

    if (attemptNumber > 1) {
        detail += `Previous fix attempt(s) did not resolve the issue. Try a different approach.\n`;
    }

    detail += `Please fix these errors in the code.`;

    return userSummary + detail;
}
